// src/Dashboard.js
import React, { useMemo, useState, useEffect } from "react";
import Map from "./Map";

const detectScanType = (text) => {
  if (!text || !text.trim()) return null;
  const t = text.toLowerCase();
  // Heuristics:
  // netsh dumps often include "BSSID", "Signal", "Network type"
  // macOS wlscan/wifiscan lines like "bssid=xx:xx..." and "rssi=-5x"
  if (t.includes("bssid=") && t.includes("rssi=")) return "wifiscan";
  if (t.includes("bssid") && t.includes("signal")) return "netsh";
  return null;
};

const Section = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card">
      <div className="card-head" onClick={() => setOpen(!open)}>
        <span>{title}</span>
        <span className={`chev ${open ? "open" : ""}`}>▸</span>
      </div>
      {open && <div className="card-body">{children}</div>}
    </div>
  );
};

const Dashboard = ({
  apiKey,
  onLogout,
  onSubmitNetsh,     // (text) => {results}
  onSubmitWifiscan,  // (text) => {latitude, longitude, ...} OR {results}
  onFetchHistory,    // () => {results}
  onSelectSSID       // (entry) => void
}) => {
  const [input, setInput] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  const filtered = useMemo(() => {
    if (!filter.trim()) return results;
    const f = filter.toLowerCase();
    return results.filter((r) =>
      (r.ssid || "").toLowerCase().includes(f) ||
      (r.bssid || "").toLowerCase().includes(f)
    );
  }, [results, filter]);

  const copyApiKey = async () => {
    if (!apiKey) return;
    await navigator.clipboard.writeText(apiKey);
    setToast("API key copied");
    setTimeout(() => setToast(""), 1200);
  };

  const handleSubmit = async () => {
    if (!input.trim()) return;
    setBusy(true);
    try {
      const type = detectScanType(input);
      if (type === "wifiscan") {
        const res = await onSubmitWifiscan(input);
        // wifiscan returns a single location (lat/lng/accuracy).
        // Normalize to results list with a pseudo-record:
        if (res?.latitude && res?.longitude) {
          const item = {
            ssid: "wifiscan centroid",
            bssid: "(multiple)",
            latitude: res.latitude,
            longitude: res.longitude,
            query_date: res.query_date || new Date().toISOString(),
            signal_strength: null,
            accuracy: res.accuracy
          };
          setResults((prev) => [item, ...prev]);
          setSelected(item);
          onSelectSSID?.(item);
        } else if (res?.results) {
          setResults(res.results);
        }
      } else {
        // Default to netsh if unknown
        const res = await onSubmitNetsh(input);
        if (res?.results) setResults(res.results);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleHistory = async () => {
    setBusy(true);
    try {
      const res = await onFetchHistory();
      if (res?.results) setResults(res.results);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (apiKey) handleHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  const handleSelect = (entry) => {
    setSelected(entry);
    onSelectSSID?.(entry);
  };

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="brand">
          <span className="dot" />
          <span>FindIt · BSSID Locator</span>
        </div>
        <div className="topbar-actions">
          <div className="api-pill" title={apiKey || "No API key"}>
            <span>API Key:</span>
            <code>{apiKey ? apiKey.replace(/.(?=.{6})/g, "•") : "—"}</code>
            <button className="btn ghost" onClick={copyApiKey}>Copy</button>
          </div>
          <button className="btn" onClick={onLogout}>Logout</button>
        </div>
      </div>

      <div className="layout">
        {/* LEFT: inputs and docs */}
        <aside className="left">
          <Section title="Paste scan output">
            <textarea
              className="scan-input"
              rows={10}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste netsh or macOS wifiscan output…"
            />
            <div className="row">
              <button className="btn primary" onClick={handleSubmit} disabled={busy}>
                {busy ? "Submitting…" : "Submit"}
              </button>
              <button className="btn" onClick={handleHistory} disabled={busy}>
                {busy ? "Loading…" : "Show History"}
              </button>
            </div>
            <div className="hint">
              Auto-detects: <code>netsh</code> (Windows) or <code>wifiscan/wlscan</code> (macOS).
            </div>
          </Section>

          <Section title="API endpoints" defaultOpen={false}>
            <div className="endpoint">
              <h4>POST /query/netsh</h4>
              <pre>{`curl -X POST https://findit.sarresearch.com/api/query/netsh \\
  -H "x-api-key: {API_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{"netsh_output":"{netsh output}"}'`}</pre>
            </div>

            <div className="endpoint">
              <h4>POST /query/wifiscan</h4>
              <pre>{`curl -X POST https://findit.sarresearch.com/api/query/wifiscan \\
  -H "x-api-key: {API_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{"raw_output":"{macOS wifiscan / wlscan output}"}'`}</pre>
            </div>

            <div className="endpoint">
              <h4>GET /history</h4>
              <pre>{`curl -X GET https://findit.sarresearch.com/api/history \\
  -H "x-api-key: {API_KEY}"`}</pre>
            </div>

            <div className="endpoint">
              <h4>GET /bssid/{`{BSSID}`}</h4>
              <pre>{`curl -X GET https://findit.sarresearch.com/api/bssid/{BSSID} \\
  -H "x-api-key: {API_KEY}"`}</pre>
            </div>
          </Section>

          <Section title="Tips" defaultOpen={false}>
            <ul className="tips">
              <li>Windows: <code>netsh wlan show networks mode=bssid</code></li>
              <li>macOS: <code>sudo wdutil diagnose -q  -f OUTPUTDIR > /dev/null 2>&1; tar -xOf OUTPUTDIR/WirelessDiagnostics* *wifi_scan.txt</code></li>
              <li>Click a result to snap map & open a Google Maps link.</li>
            </ul>
          </Section>
        </aside>

        {/* CENTER: map */}
        <main className="center">
          <Map selected={selected} />
        </main>

        {/* RIGHT: results & details */}
        <aside className="right">
          <div className="card">
            <div className="card-head">
              <span>Found SSIDs / History</span>
            </div>
            <div className="card-body">
              <input
                className="search"
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter by SSID or BSSID…"
              />
              <ul className="result-list">
                {filtered.map((r, i) => (
                  <li key={`${r.bssid || "row"}-${i}`} onClick={() => handleSelect(r)}>
                    <div className="title">{r.ssid || "(unknown SSID)"} </div>
                    <div className="meta">
                      <span>{r.bssid}</span>
                      {r.latitude && r.longitude && (
                        <a
                          href={`https://maps.google.com/?q=${r.latitude},${r.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Open in Maps
                        </a>
                      )}
                    </div>
                  </li>
                ))}
                {!filtered.length && <li className="muted">No results</li>}
              </ul>
            </div>
          </div>

          {selected && (
            <div className="card">
              <div className="card-head">
                <span>Selected</span>
              </div>
              <div className="card-body">
                <div className="kv">
                  <label>SSID</label>
                  <div>{selected.ssid || "—"}</div>
                </div>
                <div className="kv">
                  <label>BSSID</label>
                  <div>{selected.bssid || "—"}</div>
                </div>
                <div className="kv">
                  <label>Coordinates</label>
                  <div>
                    {selected.latitude && selected.longitude
                      ? `${selected.latitude.toFixed(6)}, ${selected.longitude.toFixed(6)}`
                      : "—"}
                  </div>
                </div>
                <div className="kv">
                  <label>Signal</label>
                  <div>{selected.signal_strength ?? "—"}</div>
                </div>
                {selected.latitude && selected.longitude && (
                  <a
                    className="btn link"
                    href={`https://maps.google.com/?q=${selected.latitude},${selected.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in Google Maps
                  </a>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
};

export default Dashboard;

